import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from '../../environments/environment';
import { DatastoreService } from './index';
import { UUID } from 'angular2-uuid';
import moment from 'moment-timezone';
import { catchError, firstValueFrom, map, Observable, throwError } from 'rxjs';

/**
 * The `mm` of a JRNI timestamp, read straight off the string. JRNI reports the
 * slot in the company's own timezone, so the minutes are already the ones the
 * calendar shows — re-parsing through the viewer's local timezone would shift
 * them for anyone on a half-hour offset.
 */
function startMinute(start: unknown): string | null {
	const match = /T\d{2}:(\d{2})/.exec(`${start}`);
	return match ? match[1] : null;
}

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	config: any;
	selectedSlot: any;
	availableTimesService: any;
	availableDaysService: any;

	/**
	 * Registrars (JRNI person ids) whose slots may only start at one minute
	 * offset past the hour. Anyone not listed here is unrestricted.
	 *   15316 OMTH - East, 15337 OMTH - East West, 15318 OMTH - West
	 */
	private static readonly PERSON_START_MINUTE: Record<string, string> = {
		'15316': '45',
		'15337': '30',
		'15318': '15',
	};

	constructor(private http: HttpClient, private dataStore: DatastoreService) {}

	async register(registerForm: any, companyid: any) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			const body = {
				email: registerForm.email,
				first_name: registerForm.first_name,
				last_name: registerForm.last_name,
				mobile: registerForm.mobile,
				address1: registerForm.address1,
				address2: registerForm.address2,
				address4: registerForm.address4,
				consent: registerForm.consent,
				send_welcome_email: registerForm.send_welcome_email,
				member_type: registerForm.member_type,
				password: registerForm.password,
				q: registerForm.q,
			};

			return await this.http
				.post(`${environment.apiServer}/company/${companyid}/client`, body, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async registerClient(registerForm: any, companyid: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${companyid}`,
			};
			const body = {
				email: registerForm.email,
				first_name: registerForm.first_name,
				last_name: registerForm.last_name,
				mobile: registerForm.mobile,
				datetime: moment().utc().tz('Europe/London').format(),
				address1: registerForm.address1,
				address2: registerForm.address2,
				address4: registerForm.address4,
				postcode: registerForm.postcode,
				consent: registerForm.consent,
				// "password": registerForm.password,
				// "confirm_password": registerForm.password,
				send_welcome_email: registerForm.send_welcome_email,
				send_email: registerForm.send_welcome_email,
				member_type: registerForm.member_type,
				questions: registerForm.answers,
				client_type: 'Member',
			};

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyid}/create-client`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async submitRegistration(registerForm: any, companyid: any) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			const body = {
				id: UUID.UUID(),
				email: registerForm.email,
				status: 0,
				data: {
					first_name: registerForm.first_name,
					last_name: registerForm.last_name,
					mobile: registerForm.mobile,
					datetime: moment().utc().tz('Europe/London').format(),
					address1: registerForm.address1,
					address2: registerForm.address2,
					address4: registerForm.address4,
					postcode: registerForm.postcode,
					consent: registerForm.consent,
					send_welcome_email: registerForm.send_welcome_email,
					member_type: registerForm.member_type,
					q: registerForm.answers,
				},
			};

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyid}/registrations`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async login(loginForm: any) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			const body = {
				email: loginForm.email,
				password: loginForm.password,
			};

			return await this.http
				.post(`${environment.apiServer}/login`, body, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getPendingRegistrationByEmail(companyid: any, emailURI: string) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(
					`${environment.apiServer}/company/${companyid}/registrations/${emailURI}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getWorkflowByEmail(email: string) {
		try {
			const headers = { 'Content-Type': 'application/json' };

			return await this.http
				.get(`${environment.apiServer}/client/workflow-email/${email}`, {
					headers,
				})
				.toPromise();
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	async findClientByEmail(companyid: any, emailURI: string) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(
					`${environment.apiServer}/company/${companyid}/client/email/${emailURI}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getClientDetails(companyid: any) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(`${environment.apiServer}/company/${companyid}/client-details`, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getCompany(companyid: any) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(`${environment.apiServer}/company/${companyid}`, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getCompanies() {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(`${environment.apiServer}/companies`, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getChildCompanies(company_id: number) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(`${environment.apiServer}/company/${company_id}/children`, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getResource(company_id: number, resource_id: number) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(
					`${environment.apiServer}/company/${company_id}/resource/${resource_id}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getResources(company_id: number) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(`${environment.apiServer}/company/${company_id}/resources`, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}
	async getServiceResources(company_id: number, service_id: number) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(
					`${environment.apiServer}/company/${company_id}/service/${service_id}/resources`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getServices(company_id: number) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(`${environment.apiServer}/company/${company_id}/services`, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getService(company_id: number, service_id: number) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(
					`${environment.apiServer}/company/${company_id}/service/${service_id}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getServiceCompanies() {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(`${environment.apiServer}/service/companies`, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getBookings(company_id: number, memberid: number) {
		const authToken = this.dataStore.authToken;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${company_id}`,
			};
			const r = await this.http
				.get(
					`${environment.apiServer}/company/${company_id}/member/${memberid}/bookings`,
					{ headers }
				)
				.toPromise();
			return r;
		} catch (error) {
			console.log(error);
			return (error as Error).message;
		}
	}

	async getPastBookings(company_id: number, memberid: number) {
		const authToken = this.dataStore.authToken;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${company_id}`,
			};
			const r = await this.http
				.get(
					`${environment.apiServer}/company/${company_id}/member/${memberid}/past-bookings`,
					{ headers }
				)
				.toPromise();
			return r;
		} catch (error) {
			console.log(error);
			return (error as Error).message;
		}
	}

	async getBookingsAttendees(company_id: number, memberid: number, id: number) {
		const authToken = this.dataStore.authToken;

		try {
			const headers = {
				'Content-Type': 'application/json',
			};
			return await this.http
				.get(
					`${environment.apiServer}/company/${company_id}/member/${memberid}/bookings/${id}/attendees`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getBooking(company_id: number, bookingid: number) {
		// const authToken = this.dataStore.authToken;

		try {
			const headers = {
				'Content-Type': 'application/json',
				// 'authtoken': authToken,
				'x-company-id': `${company_id}`,
			};
			return await this.http
				.get(
					`${environment.apiServer}/company/${company_id}/booking/${bookingid}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			console.log(error);
			return (error as Error).message;
		}
	}

	async getMemberBooking(
		company_id: number,
		memberid: number,
		bookingId: number
	) {
		const authToken = this.dataStore.authToken;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${company_id}`,
			};
			return await this.http
				.get(
					`${environment.apiServer}/company/${company_id}/member/${memberid}/bookings/${bookingId}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async patchMember(company_id: number, member_id: number, body: any) {
		const authToken = this.dataStore.authToken;
		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${member_id}`,
				authtoken: authToken,
				'x-company-id': `${company_id}`,
			};

			return await this.http
				.patch(
					`${environment.apiServer}/company/${company_id}/member/${member_id}`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getServiceDates(
		company_id: number,
		service_id: number,
		start_date: String,
		end_date: String,
		resource: any,
		duration: any
	) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			let url = `${environment.apiServer}/company/${company_id}/service/${service_id}/days/${start_date}/${end_date}`;
			let join = '?';
			if (resource) {
				url = `${url}${join}resources=${resource}`;
				join = '&';
			}
			if (duration) {
				url = `${url}${join}duration=${duration}`;
			}
			return await this.http.get(`${url}`, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getBookedDates(
		company_id: number,
		service_id: number,
		resource_id: number,
		date: String
	) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			let url = `${environment.apiServer}/company/${company_id}/service/${service_id}/resource/${resource_id}/unavailable/${date}`;
			return await this.http.get(`${url}`, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async putCheckinBooking(
		company_id: number,
		memberid: number,
		bookingid: number
	) {
		const authToken = this.dataStore.authToken;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${company_id}`,
			};

			return await this.http
				.put(
					`${environment.apiServer}/bookings/${bookingid}/checkin/${memberid}`,
					{},
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getServiceTimes(
		company_id: number,
		service_id: number,
		start_date: String,
		end_date: String,
		resource: any,
		duration: any = null,
		bookingId: any = null,
		resources: any = null
	) {
		try {
			// Extending a booking has to discount the booking's own slot, and
			// ignore_booking is only supported by the v5-backed BookingLab endpoint,
			// so that path is left exactly as it was. Everything else goes to JRNI
			// v1, the only version that returns person_id — which the slot filter
			// below keys off (RWRSPI-1097).
			if (bookingId !== null) {
				const headers = { 'Content-Type': 'application/json' };
				// A resource does a different search. Needed so we use the v5 api
				// which gives us nicer formatting and works with ignoreBooking.
				let url = resource
					? `${environment.apiServer}/company/${company_id}/service/${service_id}/resource/${resource}/times/${start_date}/${end_date}`
					: `${environment.apiServer}/company/${company_id}/service/${service_id}/times/${start_date}/${end_date}`;

				url = `${url}?ignore_booking=${bookingId}`;
				if (duration) {
					url = `${url}&duration=${duration}`;
				}
				if (!resource && resources) {
					url = `${url}&resources=${resources}`;
				}
				return await this.http.get(url, { headers }).toPromise();
			}

			const params: string[] = [
				'only_available=true',
				`service_id=${service_id}`,
				`start_date=${start_date}`,
				`end_date=${end_date}`,
			];
			if (duration) {
				params.push(`duration=${duration}`);
			}
			// Keep the room restriction the v5 path got from its /resource/ URL —
			// without it JRNI answers for every room on the service.
			if (resource) {
				params.push(`resource_id=${resource}`);
			} else if (resources) {
				params.push(`resource_ids=${resources}`);
			}

			return await this.getTimesFiltered(company_id, params.join('&'));
		} catch (error) {
			return (error as Error).message;
		}
	}

	/**
	 * JRNI returns a slot per registrar, and some registrars only ever start on
	 * one minute offset past the hour. Drop their slots at every other offset so
	 * the calendar only offers times they actually work (RWRSPI-1097).
	 */
	async getTimesFiltered(company_id: number, queryParams: string) {
		const res: any = await this.getTimes(company_id, queryParams);
		const times = res?._embedded?.times;

		if (!Array.isArray(times)) {
			// getTimes swallows failures into a message string. Report no
			// availability rather than throwing out of the calendar.
			console.error('Times request failed', res);
			return { times: [] };
		}

		return {
			...res._embedded,
			times: times.filter((slot: any) => {
				const minute = ApiService.PERSON_START_MINUTE[`${slot?.person_id}`];
				return minute === undefined || startMinute(slot?.start) === minute;
			}),
		};
	}

	async getTimes(company_id: number, queryParams: string) {
		try {
			const headers = {
				'x-company-id': `${company_id}`,
				'app-id': '96a0e91a7c89a6ccaa54c4b58e537736c3aa511876a4',
			};
			return await this.http
				.get(
					`${
						JSON.parse(this.dataStore.config).host
					}/api/v1/${company_id}/times?${queryParams}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getCheckoutBooking(
		company_id: number,
		memberid: number,
		bookingid: number
	) {
		const authToken = this.dataStore.authToken;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${company_id}`,
			};
			return await this.http
				.put(
					`${environment.apiServer}/bookings/${bookingid}/checkout/${memberid}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	// Sends the extended_ceremony_enquiry liquid template (RWRSPI-971). Called
	// once per recipient — see ExtendStateService.sendEnquiryEmails().
	async sendExtendedCeremonyEnquiryEmail(
		company_id: number,
		to: string,
		from: string,
		bookingid: string
	): Promise<Record<string, any>> {
		const url = `${environment.apiServer}/company/${company_id}/email`;
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${environment.clientToken}`,
		};
		const body = {
			slot: `${bookingid}`,
			entity: 'booking',
			to,
			from,
			subject: 'Extended ceremony enquiry',
			type: 'extended_ceremony_enquiry',
		};

		return firstValueFrom(
			this.http
				.post(url, body, { headers })
				.pipe(
					catchError((err) =>
						throwError(() => new Error(err.error || err.message))
					)
				)
		);
	}

	// Sends the extended_ceremony_confirmation liquid template to the customer
	// after a paid extension (RWRSPI-1002).
	async sendExtendedCeremonyConfirmationEmail(
		company_id: number,
		to: string,
		from: string,
		bookingid: string
	): Promise<Record<string, any>> {
		const url = `${environment.apiServer}/company/${company_id}/email`;
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${environment.clientToken}`,
		};
		const body = {
			slot: `${bookingid}`,
			entity: 'booking',
			to,
			from,
			subject: 'Additional time booked for your ceremony',
			type: 'extended_ceremony_confirmation',
		};

		return firstValueFrom(
			this.http
				.post(url, body, { headers })
				.pipe(
					catchError((err) =>
						throwError(() => new Error(err.error || err.message))
					)
				)
		);
	}

	// Sends the extend enquiry admin email body to the liquid endpoint.
	async sendExtendedCeremonyAdminEnquiryEmail(
		company_id: number,
		booking_id: number,
		to: string,
		from: string,
		subject: string,
		body: string
	): Promise<Record<string, any>> {
		const url = `${environment.apiServer}/company/${company_id}/email/liquid`;
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${environment.clientToken}`,
		};
		const requestBody = {
			slot: `${booking_id}`,
			entity: 'booking',
			to,
			from,
			subject,
			type: body,
		};

		return firstValueFrom(
			this.http
				.post(url, requestBody, { headers })
				.pipe(
					catchError((err) =>
						throwError(() => new Error(err.error || err.message))
					)
				)
		);
	}

	// Emails the customer their add-on details (pre-rendered HTML) via the liquid endpoint.
	async sendExtendedCeremonyAddOnEmail(
		company_id: number,
		booking_id: number,
		to: string,
		from: string,
		subject: string,
		body: string
	): Promise<Record<string, any>> {
		const url = `${environment.apiServer}/company/${company_id}/email/liquid`;
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${environment.clientToken}`,
		};
		const requestBody = {
			slot: `${booking_id}`,
			entity: 'booking',
			to,
			from,
			subject,
			type: body,
		};

		return firstValueFrom(
			this.http
				.post(url, requestBody, { headers })
				.pipe(
					catchError((err) =>
						throwError(() => new Error(err.error || err.message))
					)
				)
		);
	}

	// Office-only alert email; `type` names a server-side liquid template and
	// slot/entity target the booking so the template can read its data.
	async sendBackOfficeAlertEmail(
		company_id: number,
		booking_id: number | string,
		to: string,
		from: string,
		subject: string,
		templateKey: string
	): Promise<Record<string, any>> {
		const url = `${environment.apiServer}/company/${company_id}/email`;
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${environment.clientToken}`,
		};
		const body = {
			slot: `${booking_id}`,
			entity: 'booking',
			to,
			from,
			subject,
			type: templateKey,
		};

		return firstValueFrom(
			this.http
				.post(url, body, { headers })
				.pipe(
					catchError((err) =>
						throwError(() => new Error(err.error || err.message))
					)
				)
		);
	}

	// Records an "email_sent" interaction against the booking's audit log so it
	// shows up on the booking timeline. Called after an extend email has been
	// sent successfully — see ExtendStateService.sendEnquiryEmails().
	async bookingTimeline(
		company_id: number,
		purchaseId: any,
		memberId: any,
		memberEmail: string,
		subject: string,
		from: string
	): Promise<Record<string, any>> {
		const url = `${environment.apiServer}/company/${company_id}/auditlog/interactions`;
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${environment.clientToken}`,
		};
		const body = {
			data: {
				email_type: subject,
				from,
				to: memberEmail,
				subject,
				email_body: '',
				content_type: 'text/html; charset=UTF-8',
			},
			total_id: purchaseId,
			client_id: memberId,
			interaction_type: 'email_sent',
		};

		return firstValueFrom(
			this.http
				.post(url, body, { headers })
				.pipe(
					catchError((err) =>
						throwError(() => new Error(err.error || err.message))
					)
				)
		);
	}

	// async getServiceDates(company_id: number, service_id: number, start_date: Date, duration: number) {
	//   let startDate = start_date.toISOString().split("T")[0];
	//   let endDateUnix = await this.utils.addDays(start_date, duration);
	//   let endDate = new Date(endDateUnix).toISOString().split("T")[0];
	//   try {
	//     const headers = { 'Content-Type': 'application/json' };
	//     return await this.http.get(`${environment.apiServer}/company/${company_id}/service/${service_id}/days/${startDate}/${endDate}`, { headers }).toPromise();
	//   } catch (error) {
	//     return (error as Error).message;
	//   }
	// }

	// async getServiceTimes(company_id: number, service_id: number, date: Date) {
	//   try {
	//     const headers = { 'Content-Type': 'application/json' };
	//     return await this.http.get(`${environment.apiServer}/company/${company_id}/services`, { headers }).toPromise();
	//   } catch (error) {
	//     return (error as Error).message;
	//   }
	// }

	async getCancelBooking(
		company_id: number,
		memberid: number,
		bookingid: number
	) {
		const authToken = this.dataStore.authToken;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${company_id}`,
			};
			return await this.http
				.delete(
					`${environment.apiServer}/company/${company_id}/member/${memberid}/bookings/${bookingid}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async removeAttendee(
		company_id: number,
		memberid: number,
		bookingid: number,
		attendee_id: number
	) {
		const authToken = this.dataStore.authToken;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${company_id}`,
			};

			return await this.http
				.delete(
					`${environment.apiServer}/company/${company_id}/member/${memberid}/bookings/${bookingid}/attendees/${attendee_id}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async createBasket() {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const companyid = this.dataStore.childCompany;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const body = {
				company_id: companyid,
			};

			return await this.http
				.post(`${environment.apiServer}/basket`, body, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getBaskets() {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const companyid = this.dataStore.childCompany;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};

			return await this.http
				.get(`${environment.apiServer}/baskets`, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async deleteBaskets() {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.client.id;
		const companyid = JSON.parse(this.dataStore.config).company;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};

			return await this.http
				.delete(`${environment.apiServer}/baskets`, { headers })
				.toPromise();
		} catch (error) {
			console.log(error);
			return (error as Error).message;
		}
	}

	async addServiceItem(basket_id: any) {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const companyid = this.dataStore.childCompany;
		const serviceid = this.dataStore.bookingDetails.serviceId;
		const resourceid = this.dataStore.bookingDetails.resourceId;
		const start = this.dataStore.bookingDetails.time;
		const duration = this.dataStore.bookingDetails.duration;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const body = {
				company_id: companyid,
				service_id: serviceid,
				resource_id: resourceid,
				start: start,
				duration: parseInt(duration),
				questions: [],
			};

			return await this.http
				.post(
					`${environment.apiServer}/baskets/${basket_id}/service_item`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async updateServiceItem(questionsObj: any) {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const companyid = this.dataStore.childCompany;
		const serviceid = this.dataStore.bookingDetails.serviceId;
		const resourceid = this.dataStore.bookingDetails.resourceId;
		const basketid = this.dataStore.basket.id;
		const serviceitemid = this.dataStore.serviceItem.id;
		const start = this.dataStore.serviceItem.start;
		const duration = this.dataStore.bookingDetails.duration;
		const questions = questionsObj;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const body = {
				company_id: companyid,
				service_id: serviceid,
				resource_id: resourceid,
				start: start,
				duration: parseInt(duration),
				questions: questions,
			};

			return await this.http
				.put(
					`${environment.apiServer}/baskets/${basketid}/service_item/${serviceitemid}`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async checkout() {
		const authToken = this.dataStore.authToken;
		const companyid = this.dataStore.company.id;
		const basketid = this.dataStore.basket.id;
		const serviceid = this.dataStore.bookingDetails.serviceId;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${this.dataStore.client.id}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const body = {
				service_id: serviceid,
				customer_id: this.dataStore.client.id,
				date: this.dataStore.selectedDate,
				no_notifications: true,
			};

			return await this.http
				.post(
					`${environment.apiServer}/baskets/${basketid}/public-checkout`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async dayCheckout() {
		const companyid = JSON.parse(this.dataStore.config).company;
		const serviceid = this.dataStore.bookingDetails.serviceId;
		const authToken = this.dataStore.authToken;
		const start = this.dataStore.bookingDetails.time;
		const duration = this.dataStore.bookingDetails.duration;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${this.dataStore.client.id}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const body = {
				service_id: serviceid,
				customer_id: this.dataStore.client.id,
				date: this.dataStore.selectedDate,
				no_notifications: true,
			};

			//https://m5nius1kad.execute-api.eu-west-2.amazonaws.com/1/company/52481/service/112376/day-based/checkout-public
			return await this.http
				.post(
					`${environment.apiServer}/company/${companyid}/service/${serviceid}/day-based/checkout-public`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async moveBooking(booking: any, date: any, time: any, duration: any) {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const companyid = this.dataStore.childCompany;
		const bookingid = booking.id;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const body = {
				date: date,
				time: time,
				duration: duration,
			};

			return await this.http
				.put(
					`${environment.apiServer}/company/${companyid}/member/${memberid}/bookings/${bookingid}/move`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async questions(slot: any) {
		// const authToken = this.dataStore.authToken;
		// const memberid = this.dataStore.member.id;
		const companyid = JSON.parse(this.dataStore.config).company;
		const detailgroupid = slot.selectedService.detail_group_id;

		try {
			const headers = {
				'Content-Type': 'application/json',
			};

			return await this.http
				.get(
					`${environment.apiServer}/company/${companyid}/questions/${detailgroupid}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async attendees() {
		const companyid = this.dataStore.childCompany;
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const serviceid = this.dataStore.serviceItem.id;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const basketid = this.dataStore.basket.id;

			return await this.http
				.get(
					`${environment.apiServer}/baskets/${basketid}/service_items/${serviceid}/attendees`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async addAttendee(
		attendee: any,
		company_id: number = -1,
		booking: number = -1
	) {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const companyid = this.dataStore.childCompany;

		try {
			const body = {
				email: attendee.email,
				telephone: attendee.mobile,
				// "type": "customer",
				// "primary_attendee": false,
				name: attendee.name,
				first_name: attendee.first_name,
				last_name: attendee.last_name,
			};
			if (company_id === -1) {
				const headers = {
					'Content-Type': 'application/json',
					'x-member-id': `${memberid}`,
					authtoken: authToken,
					'x-company-id': `${companyid}`,
				};
				const basketid = this.dataStore.basket.id;
				const serviceid = this.dataStore.serviceItem.id;
				return await this.http
					.post(
						`${environment.apiServer}/baskets/${basketid}/service_items/${serviceid}/attendees`,
						body,
						{ headers }
					)
					.toPromise();
			} else {
				const headers = {
					'Content-Type': 'application/json',
					'x-member-id': `${memberid}`,
					authtoken: authToken,
					'x-company-id': `${company_id}`,
				};
				return await this.http
					.post(
						`${environment.apiServer}/company/${company_id}/member/${memberid}/bookings/${booking}/attendees`,
						body,
						{ headers }
					)
					.toPromise();
			}
		} catch (error) {
			return (error as Error).message;
		}
	}

	async updateBooking(
		body: any,
		company_id: number,
		booking: number,
		useToken: boolean
	) {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		console.log(this.dataStore);
		const companyid = this.dataStore.company.id;
		// const basketid = this.dataStore.basket.id;
		// const serviceid = this.dataStore.serviceItem.id;

		try {
			const headers: any = {
				'Content-Type': 'application/json',
				'x-company-id': `${companyid}`,
			};

			if (useToken) {
				headers['x-member-id'] = `${memberid}`;
				headers['authtoken'] = authToken;
			}

			return await this.http
				.put(
					`${environment.apiServer}/company/${company_id}/booking/${booking}`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			console.log(error);
			return (error as Error).message;
		}
	}

	async updateMemberBooking(
		body: any,
		company_id: number,
		memberId: number,
		booking: number
	) {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const companyid = this.dataStore.company.id;
		// const basketid = this.dataStore.basket.id;
		// const serviceid = this.dataStore.serviceItem.id;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};

			return await this.http
				.put(
					`${environment.apiServer}/company/${company_id}/member/${memberId}/bookings/${booking}`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			console.log(error);
			return (error as Error).message;
		}
	}

	async searchAttendee(searchString: String) {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const companyid = this.dataStore.company;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const body = {
				term: searchString,
			};

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyid}/client/search`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getResourceMap() {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(`${environment.apiServer}/config/resource-map`, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getResourceAvailability(
		date: String,
		resourceList: any,
		duration: any
	) {
		const companyid = this.dataStore.bookingDetails.childCompanyId;
		const serviceid = this.dataStore.bookingDetails.serviceId;
		console.log(`service id is ${serviceid}`);
		let url = `${environment.apiServer}/company/${companyid}/service/${serviceid}/times/${date}/${date}?resources=${resourceList}&duration=${duration}`;
		try {
			// get config to check for cache
			const config: any = sessionStorage.getItem('config');
			const configObj = JSON.parse(config);
			if (configObj.hasOwnProperty('cache')) {
				if (configObj.cache.hasOwnProperty('cache_find_button')) {
					if (configObj.cache.cache_find_button) {
						url = `${url}&cache_find=true`;
					}
				}
			}
			const headers = { 'Content-Type': 'application/json' };
			return await this.http.get(url, { headers }).toPromise();
			// return await this.http.get(`${environment.apiServer}/company/${companyid}/service/${serviceid}/resource/${resourceid}/times/${date}/${date}`, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getMeetingRoomBookings(
		companyid: any,
		serviceid: any,
		resourceid: any,
		date: any
	) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(
					`${environment.apiServer}/company/${companyid}/service/${serviceid}/resource/${resourceid}/bookings/${date}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async bookingCheckin(bookingid: Number, memberid: Number) {
		const companyid = this.dataStore.company;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${companyid}`,
			};
			const body = {};

			return await this.http
				.put(
					`${environment.apiServer}/bookings/${bookingid}/checkin/${memberid}`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async bookingCheckout(bookingid: Number, memberid: Number) {
		const companyid = this.dataStore.company;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${companyid}`,
			};
			const body = {};

			return await this.http
				.put(
					`${environment.apiServer}/bookings/${bookingid}/checkout/${memberid}`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async bookingNoshow(bookingid: Number, memberid: Number) {
		const companyid = this.dataStore.company;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${companyid}`,
			};
			const body = {};

			return await this.http
				.put(
					`${environment.apiServer}/bookings/${bookingid}/noshow/${memberid}`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async bookingCancel(
		companyid: Number,
		bookingid: Number,
		notify: boolean,
		reason: string
	) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${companyid}`,
			};
			const body = {
				notify: notify,
				reason: reason,
			};

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyid}/booking/${bookingid}/cancel`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async addNotesToBooking(companyid: Number, bookingid: Number, note: String) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${companyid}`,
			};
			const body = {
				type: 'private_notes',
				note: note,
			};

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyid}/booking/${bookingid}/note`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async addToMessageQueue(action: String, booking: any, attendee: String) {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const companyid = this.dataStore.childCompany;
		const parentid = this.dataStore.bookingDetails.parentCompanyId;
		const parent = this.dataStore.childCompanies.find(
			(c: any) => c.id == parentid
		);
		const company = this.dataStore.companies.find(
			(c: any) => c.id == companyid
		);
		const serviceid = this.dataStore.bookingDetails.serviceId;
		const client = this.dataStore.client;

		// if we are booking a desk, set the message_type to deskInvite.
		let message_type = 'email';
		if (this.dataStore.selectedService.hasOwnProperty('extra')) {
			if (this.dataStore.selectedService.extra.hasOwnProperty('service_type')) {
				if (
					this.dataStore.selectedService.extra.service_type === 'Book a desk'
				) {
					// load the question from config
					const config = sessionStorage.getItem('config') as any;
					const configObj = JSON.parse(config);
					if (configObj.hasOwnProperty('deskBehalfOptions')) {
						const configQuestion = configObj.deskBehalfOptions.question;
						if (this.dataStore.member.q.hasOwnProperty(configQuestion)) {
							if (this.dataStore.member.q[configQuestion].answer === 'Yes') {
								message_type = 'deskInvite';
							}
						}
					}
				}
			}
		}

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const body = {
				client: client,
				company: company,
				parent: parent,
				service: serviceid,
				action: action,
				booking: booking,
				attendee: attendee,
				message_type: message_type,
			};

			const url = `${environment.apiServer}/company/${companyid}/messages`;

			return await this.http.post(url, body, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async addExistingMessageToQueue(
		action: String,
		booking: any,
		attendee: String
	) {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const companyid = this.dataStore.childCompany;
		const company: any = await this.getCompany(booking.company_id);
		const parentid = company.parent;
		const parent = await this.getCompany(parentid);
		const serviceid = booking.service_id;
		const client = this.dataStore.client;

		const service = (await this.getService(company.id, serviceid)) as any;

		let message_type = 'email';
		if (service.hasOwnProperty('extra')) {
			if (service.extra.hasOwnProperty('service_type')) {
				if (service.extra.service_type === 'Book a desk') {
					// load the question from config
					const config = sessionStorage.getItem('config') as any;
					const configObj = JSON.parse(config);
					if (configObj.hasOwnProperty('deskBehalfOptions')) {
						const configQuestion = configObj.deskBehalfOptions.question;
						if (this.dataStore.member.q.hasOwnProperty(configQuestion)) {
							if (this.dataStore.member.q[configQuestion].answer === 'Yes') {
								message_type = 'deskInvite';
							}
						}
					}
				}
			}
		}

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${company.id}`,
			};
			const body = {
				client: client,
				company: company,
				parent: parent,
				service: serviceid,
				action: action,
				booking: booking,
				attendee: attendee,
				message_type: message_type,
			};

			return await this.http
				.post(`${environment.apiServer}/company/${company.id}/messages`, body, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async pollForCardScans(companyId: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
			};

			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/messages?queueType=cardScan`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getUserByCardUID(companyId: any, uid: string) {
		try {
			const headers = {
				'Content-Type': 'application/json',
			};

			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/cards/user/${uid}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getUserByCardEmail(email: string) {
		const authToken = this.dataStore.authToken;
		const companyid = this.dataStore.company;

		try {
			const headers = {
				'Content-Type': 'application/json',
			};

			return await this.http
				.get(
					`${environment.apiServer}/company/${companyid}/cards/user/email/${email}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getMemberDetails(companyId: any, uid: string) {
		try {
			const headers = {
				'Content-Type': 'application/json',
			};

			return await this.http
				.get(`${environment.apiServer}/company/${companyId}/client/${uid}`, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}
	async generateSSOToken(
		companyId: any,
		first_name: any,
		last_name: any,
		email: any
	) {
		try {
			const headers = {
				'Content-Type': 'application/json',
			};

			const bodyData = {
				first_name: `${first_name}`,
				last_name: `${last_name}`,
				email: `${email}`,
			};

			return await this.http
				.post(`${environment.apiServer}/company/${companyId}/sso`, bodyData, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async ssoLogin(companyId: any, token: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
			};

			const bodyData = {
				token: `${token}`,
			};

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyId}/sso/login`,
					bodyData,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async removeMessageFromQueue(
		companyId: any,
		messageHandle: any,
		queueType: string | null
	) {
		try {
			const headers = {
				'Content-Type': 'application/json',
			};

			let url = `${environment.apiServer}/company/${companyId}/messages/${btoa(
				messageHandle
			)}` as string;

			if (queueType && queueType.length > 0) {
				switch (queueType) {
					case 'cardScan':
						url = `${url}?queueType=cardScan`;
						break;
				}
			}

			return await this.http.delete(url, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}
	async unavailableTimes() {
		const memberid = this.dataStore.member.id;
		const companyid = this.dataStore.company;
		const childCompany = this.dataStore.childCompany;
		const serviceId = this.dataStore.bookingDetails.serviceId;
		const resourceId = this.dataStore.bookingDetails.resourceId;
		const slot = this.dataStore.bookingDetails.time;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				'x-company-id': `${companyid}`,
			};

			return await this.http
				.get(
					`${environment.apiServer}/company/${childCompany}/service/${serviceId}/resource/${resourceId}/unavailable-times/${slot}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async loadBooking(feature: any) {
		const resource = feature.resource;
		const authToken = this.dataStore.authToken;
		const companyid = this.dataStore.childCompany;
		const service = this.dataStore.selectedService;

		try {
			const headers = {
				authtoken: authToken,
				'x-company-id': `${companyid}`,
				'no-loader': `true`,
			};

			return await this.http
				.get(
					`${environment.apiServer}/company/${service.company_id}/service/${service.id}/resource/${resource.resource_id}/bookings/${resource.date}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getMeetingAttendees(company_id: number, id: number) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${company_id}`,
				'no-loader': `true`,
			};
			return await this.http
				.get(
					`${environment.apiServer}/company/${company_id}/booking/${id}/attendees`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async addCardUser(userData: any, company_id: number) {
		try {
			const headers = {
				'Content-Type': 'application/json',
			};
			return await this.http
				.post(
					`${environment.apiServer}/company/${company_id}/cards/user`,
					userData,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async createClient(clientDetails: any) {
		const companyid = JSON.parse(this.dataStore.config).company;
		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${companyid}`,
			};
			const body = {
				email: clientDetails.email_address,
				first_name: clientDetails.first_name,
				last_name: clientDetails.last_name,
				mobile: clientDetails.contact_number,
				consent: true,
				send_welcome_email: false,
			};

			return await this.http
				.post(`${environment.apiServer}/company/${companyid}/client`, body, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	//////  public basket api
	async createPublicBasket() {
		const authToken = this.dataStore.authToken;
		const companyid = JSON.parse(this.dataStore.config).company;

		try {
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const body = {
				company_id: companyid,
			};

			return await this.http
				.post(`${environment.apiServer}/basket-public`, body, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getPublicBaskets() {
		const authToken = this.dataStore.authToken;
		const memberid = this.dataStore.member.id;
		const companyid = JSON.parse(this.dataStore.config).company;

		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-member-id': `${memberid}`,
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};

			return await this.http
				.get(`${environment.apiServer}/baskets-public`, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async deletePublicBaskets() {
		const authToken = this.dataStore.authToken;
		const companyid = JSON.parse(this.dataStore.config).company;
		const memberid = this.dataStore.client.id;

		try {
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-company-id': `${companyid}`,
				'x-member-id': `${memberid}`,
			};

			return await this.http
				.delete(`${environment.apiServer}/basket-public`, { headers })
				.toPromise();
		} catch (error) {
			console.log(error);
			return (error as Error).message;
		}
	}

	async deletePublicDayBasket(service: any) {
		const authToken = this.dataStore.authToken;
		const companyid = JSON.parse(this.dataStore.config).company;
		const memberid = this.dataStore.client.id;

		try {
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-company-id': `${companyid}`,
				'x-member-id': `${memberid}`,
				nounspinn: 'true',
			};

			return await this.http
				.delete(
					`${environment.apiServer}/company/${companyid}/service/${service}/day-based/basket-public`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			console.log(error);
			return (error as Error).message;
		}
	}

	async deleteSpecificPublicDayBasket(service: any, basket: any) {
		const authToken = this.dataStore.authToken;
		const companyid = JSON.parse(this.dataStore.config).company;
		const memberid = this.dataStore.client.id;

		try {
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-company-id': `${companyid}`,
				'x-member-id': `${memberid}`,
				nounspinn: 'true',
			};

			return await this.http
				.delete(
					`${environment.apiServer}/company/${companyid}/service/${service}/day-based/basket-public/${basket}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			console.log(error);
			return (error as Error).message;
		}
	}

	async addPublicServiceItem(
		basket_id: any,
		serviceid: any,
		questions: any,
		start: any,
		duration: any
	) {
		//POST /company/{company}/service/{service}/service_item-public
		const authToken = this.dataStore.authToken;
		const companyid = JSON.parse(this.dataStore.config).company;
		try {
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-company-id': `${companyid}`,
				nounspinn: 'true',
			};
			const body = {
				company_id: companyid,
				service_id: serviceid,
				questions: questions,
				start: start.start,
				duration: parseInt(duration),
			};

			if (questions === null) {
				delete body.questions;
			}

			return await this.http
				.post(
					`${environment.apiServer}/baskets/${basket_id}/service_item-public`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			console.log(error);
			return (error as Error).message;
		}
	}

	async updatePublicServiceItem(
		basket_id: any,
		serviceid: any,
		questions: any,
		start: any,
		duration: any,
		res: any
	) {
		//POST /company/{company}/service/{service}/service_item-public
		const authToken = this.dataStore.authToken;
		const companyid = JSON.parse(this.dataStore.config).company;
		try {
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const body = {
				company_id: companyid,
				service_id: serviceid,
				questions: questions,
				start: start.start,
				duration: parseInt(duration),
			};

			if (questions === null) {
				delete body.questions;
			}

			return await this.http
				.put(
					`${environment.apiServer}/baskets/${basket_id}/service_item-public/${res.id}`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			console.log(error);
			return (error as Error).message;
		}
	}

	async addPublicServiceItemDay(
		questions: any,
		selectedDate: any,
		serviceid: any
	) {
		const authToken = this.dataStore.authToken;
		const companyid = JSON.parse(this.dataStore.config).company;

		try {
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-company-id': `${companyid}`,
			};
			const body = {
				date: selectedDate,
				questions: questions,
			};

			if (questions === null) {
				delete body.questions;
			}

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyid}/service/${serviceid}/service_item-public`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getPurchase(companyId: number, serviceId: number, purchaseId: number) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/service/${serviceId}/purchase/${purchaseId}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getPurchasePayments(
		companyId: number,
		serviceId: number,
		purchaseId: number
	) {
		try {
			// payment-verify polls this every 10s while waiting for the
			// HeyCentric proxy's webhook. The 'no-loader' header keeps the
			// global ngx-spinner stable (it would otherwise blink off between
			// polls), and payment-verify manages the spinner state directly.
			const headers = {
				'Content-Type': 'application/json',
				'no-loader': 'true',
			};
			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/service/${serviceId}/purchase/${purchaseId}/payments`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async payPurchase(
		companyId: number,
		serviceId: number,
		purchaseId: number,
		purchaseData: any
	) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.put(
					`${environment.apiServer}/company/${companyId}/service/${serviceId}/purchase/${purchaseId}/pay`,
					purchaseData,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async fetchBookings(companyId: number) {
		try {
			const search = {
				from: 0,
				size: 100,
				query: {
					bool: {
						must: [
							{
								match: {
									company_id: companyId,
								},
							},
							{
								match: {
									s_status: 'Booking',
								},
							},
						],
						must_not: [
							{
								match: {
									is_cancelled: true,
								},
							},
						],
					},
				},
				sort: {
					just_date: 'desc',
				},
			};
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.post(
					`${environment.apiServer}/company/${companyId}/search/bookings`,
					search,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async searchBooking(companyId: number, ref: string) {
		try {
			const time = moment().utc().tz('Europe/London').format('YYYY-MM-DD');
			const search = {
				from: 0,
				size: 100,
				query: {
					bool: {
						must: [
							{
								match: {
									company_id: companyId,
								},
							},
							{
								match: {
									id: ref,
								},
							},
							{
								match: {
									s_status: 'Booking',
								},
							},
						],
						must_not: [
							{
								match: {
									is_cancelled: true,
								},
							},
						],
					},
				},
				sort: {
					just_date: 'desc',
				},
			};
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.post(
					`${environment.apiServer}/company/${companyId}/search/bookings`,
					search,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async searchBookingByBooker(companyId: number, member: string) {
		try {
			const time = moment().utc().tz('Europe/London').format('YYYY-MM-DD');
			const search = {
				from: 0,
				size: 100,
				query: {
					bool: {
						must: [
							{
								match: {
									company_id: companyId,
								},
							},
							{
								match: {
									member_id: member,
								},
							},
							{
								match: {
									just_date: time,
								},
							},
							{
								match: {
									s_status: 'Booking',
								},
							},
						],
						must_not: [
							{
								match: {
									is_cancelled: true,
								},
							},
						],
					},
				},
				sort: {
					just_date: 'desc',
				},
			};
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.post(
					`${environment.apiServer}/company/${companyId}/search/bookings`,
					search,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async searchClient(companyId: number, answers: any, id: any = undefined) {
		try {
			this.config = JSON.parse(this.dataStore.config);
			const must: any = [];
			must.push({
				match: {
					company_id: companyId,
				},
			});
			if (answers !== undefined) {
				if (this.config.infoQuestions.lastName) {
					must.push({
						match: {
							last_name: answers['lastName']?.value,
						},
					});
				}
				if (this.config.infoQuestions.postcode) {
					must.push({
						match: {
							postcode: answers['postcode']?.value,
						},
					});
				}
				// if (this.config.infoQuestions.dob) {
				//   must.push({
				//     "query_string": {
				//       "questions.id": this.config.dobQuestion
				//     }
				//   })
				//   must.push(
				//     {
				//       "query_string": {
				//         // "questions.answer": `${answers['dateOfBirth_year']?.value}-${answers['dateOfBirth_day']?.value}-${answers['dateOfBirth_month']?.value}T00:00:00.000Z`
				//         "questions.answer": `01-01`
				//       }
				//     },
				//   )
				// }
			}
			if (id !== undefined) {
				must.push({
					match: {
						id: id,
					},
				});
			}
			const search = {
				from: 0,
				size: 100,
				query: {
					bool: {
						must: must,
					},
				},
			};
			const headers = { 'Content-Type': 'application/json' };
			return await this.http
				.post(
					`${environment.apiServer}/company/${companyId}/search/clients`,
					search,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	//bookings/{{bookingid}}/checkin/{{attendeeid}}
	async checkIn(companyid: number, bookingid: any, attendeeid: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${companyid}`,
			};
			return await this.http
				.put(
					`${environment.apiServer}/bookings/${bookingid}/checkin-custom/${attendeeid}`,
					{},
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getOauth(companyid: number, site: string) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${companyid}`,
				'x-site': `${site}`,
				'x-client': `${site}:${companyid}`,
			};
			return await this.http
				.get(`${environment.apiServer}/oauth`, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async saveImage(companyid: number, body: any, params: string, oauth: string) {
		try {
			const headers = {
				// 'Content-Type': 'application/json',
				// 'Content-Type': 'multipart/form-data',
				'x-company-id': `${companyid}`,
				'app-id': '96a0e91a7c89a6ccaa54c4b58e537736c3aa511876a4',
				// 'app-key': 'f0bc4f65f4fbfe7b4b3b7264b655f5eb',
				upload: 'true',
				authorization: `Bearer ${oauth}`,
			};
			// const headers = new HttpHeaders()
			// headers.append("Enctype","multipart/form-data")
			// headers.append('x-company-id', `${companyid}`)
			// headers.append('app-id', 'f6b16c23')
			// headers.append('app-key', 'f0bc4f65f4fbfe7b4b3b7264b655f5eb')
			// headers.append( 'auth-token', 'i2aeWkPnlfVAkSno_-KnbA')
			// headers.append( 'upload', 'true')
			// console.log(body)
			// return await this.http.post(`${environment.apiServer}/company/${companyid}/image/link?client_id=2`, body, { headers }).toPromise();
			console.log('sending');
			return await this.http
				.post(
					`${
						JSON.parse(this.dataStore.config).host
					}/api/v1/admin/37001/image/link${params}`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getFormSchema(formId: string) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			const data = {
				company: JSON.parse(this.dataStore.config).company,
				client: `${environment.companyName}`,
				form_name: `${formId}`,
			};
			return await this.http
				.post(`${environment.apiServer}/form/schema`, data, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	// async sendNotificationEmail(booking: any) {
	//     const notificationEmail = JSON.parse(this.dataStore.config).myCeremony;
	//     const headers = { 'Content-Type': 'application/json' };
	//     const body = {
	//       "slot": booking.id,
	//       "to": notificationEmail.portal_notifications.contact_us_email_to,
	//       "from": notificationEmail.portal_notifications.contact_us_email_from,
	//       "subject": "Admin Notification",
	//       "type": "contact-us-email-to-admin",
	//     }

	//     return await this.http.post(`${environment.apiServer}/email/liquid`, body, { headers }).toPromise();
	// }

	async sendNotificationEmail(booking: any) {
		const notificationEmail = JSON.parse(this.dataStore.config).myCeremony;
		const headers = { 'Content-Type': 'application/json' };
		const body = {
			slot: booking.id,
			to: notificationEmail.portal_notifications.contact_us_email_to,
			from: notificationEmail.portal_notifications.contact_us_email_from,
			subject: 'Admin Notification',
			type: 'contact-us-email-to-admin',
		};

		return await this.http
			.post(
				`${environment.apiServer}/company/${
					JSON.parse(this.dataStore.config).grandparent_id
				}/email`,
				body,
				{ headers }
			)
			.toPromise();
	}

	async createChild(data: any, companyid: any) {
		try {
			const headers = { 'Content-Type': 'application/json' };

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyid}/dependents`,
					data,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async sendVerificationEmail(token: string, email: string) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			const data = {
				product: 'registration',
				token: `${token}`,
				email: `${email}`,
			};
			return await this.http
				.post(`${environment.apiServer}/client/verify-email`, data, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async verifyToken(token: string) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			const data = {
				token: `${token}`,
			};
			return await this.http
				.post(`${environment.apiServer}/client/verify-token`, data, { headers })
				.toPromise();
		} catch (error) {
			console.error(error);
			return (error as Error).message;
		}
	}

	async createWorkflow(email: string, password: string) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			const data = {
				password: `${password}`,
				email: `${email}`,
			};
			return await this.http
				.post(`${environment.apiServer}/client/workflow`, data, { headers })
				.toPromise();
		} catch (error) {
			console.error(error);
			return (error as Error).message;
		}
	}

	async getWorkflow(id: string) {
		try {
			const headers = { 'Content-Type': 'application/json' };

			return await this.http
				.get(`${environment.apiServer}/client/workflow/${id}`, { headers })
				.toPromise();
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	async updateWorkflow(
		email: string,
		stage: string,
		update?: any,
		save?: boolean
	) {
		try {
			const headers = { 'Content-Type': 'application/json' };
			const data: any = {
				email: `${email}`,
				stage: `${stage}`,
			};
			console.log(this.dataStore, update);
			if (update) {
				switch (stage) {
					case 'parentCarer':
						data['parent_carer'] = update;
						break;
					case 'youngPerson':
						data['young_person'] = update;
						break;
					case 'active':
						data['active'] = update;
						break;
					default:
						data['verifyEmail'] = update;
						data['verify_token'] = this.dataStore.token;
				}
			} else {
				data['verifyEmail'] = update;
				data['verify_token'] = this.dataStore.token;
			}

			if (save) {
				data['save'] = true;
				data['product'] = 'registration';
			}

			return await this.http
				.put(`${environment.apiServer}/client/workflow`, data, { headers })
				.toPromise();
		} catch (error) {
			console.error(error);
			return (error as Error).message;
		}
	}

	async getOsPostcodeData(postcode: any) {
		const headers = {
			'Content-Type': 'application/json',
		};

		return await this.http
			.get(
				`${environment.apiServer}/ordnance_survey/postcode/${postcode}?addresstype=lpi`,
				{ headers }
			)
			.toPromise();
	}

	async uploadFile(file: object) {
		const headers = {
			'Content-Type': 'application/json',
		};

		return await this.http
			.post(`${environment.apiServer}/upload`, file, { headers })
			.toPromise();
	}

	async directJrniUpload(
		config: any,
		fileInfo: any,
		purchase_id: string,
		booking_id: string
	) {
		const headers = {
			// 'Content-Type':'multipart/form-data; boundary=----WebKitFormBoundarycVkth7JcisTygmeI',
			upload: 'true',
			'app-id': 'f6b16c23',
			'app-key': 'f0bc4f65f4fbfe7b4b3b7264b655f5eb',
		};
		const body = fileInfo;
		return await this.http
			.post(
				`${config.host}api/v1/purchases/${purchase_id}/bookings/${booking_id}/attach`,
				body,
				{ headers }
			)
			.toPromise();
	}

	async jrniReadFiles(
		config: any,
		company_id: string,
		purchase_id: string,
		booking_id: string
	) {
		const headers = {
			// 'Content-Type':'multipart/form-data; boundary=----WebKitFormBoundarycVkth7JcisTygmeI',
			upload: 'true',
			'app-id': 'f6b16c23',
			'app-key': 'f0bc4f65f4fbfe7b4b3b7264b655f5eb',
		};
		return await this.http
			.get(
				`${environment.apiServer}/company/${company_id}/purchases/${purchase_id}/bookings/${booking_id}/attach`,
				{ headers }
			)
			.toPromise();
	}

	async jrniDeleteFile(
		config: any,
		company_id: string,
		purchase_id: string,
		booking_id: string,
		file_id: string
	) {
		const headers = {
			// 'Content-Type':'multipart/form-data; boundary=----WebKitFormBoundarycVkth7JcisTygmeI',
			upload: 'true',
			'app-id': 'f6b16c23',
			'app-key': 'f0bc4f65f4fbfe7b4b3b7264b655f5eb',
		};
		return await this.http
			.delete(
				`${environment.apiServer}/company/${company_id}/purchases/${purchase_id}/bookings/${booking_id}/attach/${file_id}`,
				{ headers }
			)
			.toPromise();
	}

	async deleteFile(fileId: string) {
		const headers = {
			'Content-Type': 'application/json',
		};

		return await this.http
			.delete(`${environment.apiServer}/upload/${fileId}`, { headers })
			.toPromise();
	}

	async getCustomObjectsForService(
		companyId: number,
		serviceId: number,
		appName: string,
		objectName: string,
		relationship: string
	) {
		const headers = {
			'Content-Type': 'application/json',
		};

		return await this.http
			.get(
				`${environment.apiServer}/company/${companyId}/service/${serviceId}/apps/${appName}/custom_objects/${objectName}/relationship/${relationship}`,
				{ headers }
			)
			.toPromise();
	}

	async getCustomObjectsForCompany(
		companyId: number,
		appName: string,
		objectName: string,
		relationship: string
	) {
		const headers = {
			'Content-Type': 'application/json',
		};

		return await this.http
			.get(
				`${environment.apiServer}/company/${companyId}/apps/${appName}/custom_objects/${objectName}/relationship/${relationship}`,
				{ headers }
			)
			.toPromise();
	}

	async addMetadataToBooking(
		companyId: number,
		bookingId: number,
		metadataType: string,
		data: any
	) {
		try {
			const headers = { 'Content-Type': 'application/json' };

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyId}/booking/${bookingId}/meta/${metadataType}`,
					data,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getMetadataForBooking(
		companyId: number,
		bookingId: number,
		metadataType: string
	) {
		try {
			const headers = { 'Content-Type': 'application/json' };

			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/booking/${bookingId}/meta/${metadataType}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getMessageThreads(companyId: number, bookingId: number) {
		try {
			const authToken = this.dataStore.authToken;
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};

			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/communication/threads/${bookingId}?include_sent=true`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getMessagesInThread(
		companyId: number,
		threadId: string,
		quiet: boolean
	) {
		try {
			const authToken = this.dataStore.authToken;
			let headers: any = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};
			if (quiet) {
				headers['no-loader'] = '1';
			}

			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/communication/thread/${threadId}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async markMessageAsRead(
		companyId: number,
		threadId: string,
		messageId: string
	) {
		try {
			const authToken = this.dataStore.authToken;
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};

			return await this.http
				.patch(
					`${environment.apiServer}/company/${companyId}/communication/thread/${threadId}/message/${messageId}`,
					{},
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async sendNewMessage(
		companyId: number,
		bookingId: number,
		message: string,
		catgeory: string
	) {
		try {
			const authToken = this.dataStore.authToken;
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};

			const body = {
				recipient_id: 1,
				sender_id: bookingId,
				message: message,
				category: catgeory,
			};

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyId}/communication/thread`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async sendMessageToThread(
		companyId: number,
		threadId: string,
		message: string,
		senderId: number,
		category: string
	) {
		try {
			const authToken = this.dataStore.authToken;
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};

			const body = {
				thread_id: threadId,
				sender_id: senderId,
				recipient_id: 1,
				message: message,
				category: category,
			};

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyId}/communication/thread`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getThreadsForBooking(companyId: number, bookingId: number) {
		try {
			const authToken = this.dataStore.authToken;
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};

			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/communication/threads/${bookingId}?include_sent=true`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	getServiceItemExtras(
		companyId: number,
		serviceId: number,
		category?: string,
		showHidden?: boolean
	): Observable<Record<string, any>[]> {
		const authToken = this.dataStore.authToken;
		const headers = {
			'Content-Type': 'application/json',
			authtoken: authToken,
			'x-member-id': `${this.dataStore.member.id}`,
		};

		let url = `${environment.apiServer}/company/${companyId}/extra-items?service_id=${serviceId}`;
		if (showHidden) {
			url += '&show_hidden=true';
		}

		return this.http
			.get(
				url,
				{ headers }
			)
			.pipe(
				map((response: Record<string, any>[]) => {
					if (category) {
						return response.filter(
							(item: any) => item.data?.category === category
						);
					}
					return response;
				}),
				catchError((error: any) => throwError(() => (error as Error).message))
			);
	}

	async getServiceItemExtra(
		companyId: number,
		extraItem: string,
		serviceId: number
	) {
		try {
			const authToken = this.dataStore.authToken;
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};

			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/extra-item/${extraItem}/service/${serviceId}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getServiceItemExtraLink(
		companyId: number,
		extraItem: string,
		bookingId: string
	) {
		try {
			const authToken = this.dataStore.authToken;
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};

			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/extra-item/link/${extraItem}/${bookingId}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async updateServiceItemExtraLink(
		companyId: number,
		itemId: string,
		bookingId: string,
		body: any
	) {
		try {
			const authToken = this.dataStore.authToken;
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};

			return await this.http
				.put(
					`${environment.apiServer}/company/${companyId}/extra-item/link/${itemId}/${bookingId}`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getServiceItemExtraLinksForBooking(
		companyId: number,
		bookingId: string
	) {
		try {
			const authToken = this.dataStore.authToken;
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};

			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/extra-item/link/${bookingId}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async deleteServiceItemExtraLink(
		companyId: number,
		itemId: string,
		bookingId: string,
	) {
		try {
			const authToken = this.dataStore.authToken;
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};
			return await this.http
				.delete(
					`${environment.apiServer}/company/${companyId}/extra-item/link/${itemId}/${bookingId}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async linkExtraItem(
		companyId: any,
		serviceId: any,
		bookingId: any,
		itemId: string,
		extra: any
	) {
		try {
			const authToken = this.dataStore.authToken;
			const headers = {
				'Content-Type': 'application/json',
				authtoken: authToken,
				'x-member-id': `${this.dataStore.member.id}`,
			};

			const bodyData = {
				global_service_id: serviceId,
				booking_id: `${bookingId}`,
				item_id: `${itemId}`,
				data: extra,
			};

			return await this.http
				.post(
					`${environment.apiServer}/company/${companyId}/extra-item/link`,
					bodyData,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async passwordReset(email: string, companyId: any, path: string) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${companyId}`,
			};

			const bodyData = {
				email: email,
				token_based_reset: true,
				path: `${path}`,
			};
			return await this.http
				.post(
					`${environment.apiServer}/company/${companyId}/forgot-password`,
					bodyData,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async searchVenue(companyId: any, query: string): Promise<any> {
		try {
			return await this.http
				.get(
					`${environment.apiServer}/company/${companyId}/venue-lookup?search=${encodeURIComponent(query.toLowerCase())}`
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	
	async getVenues(): Promise<any[]> {
		const headers = { 'Content-Type': 'application/json' };
		const body = { function: 'getVenues' };
		const venues: any[] = [];
		let cursor: string | undefined;

		do {
			const url = cursor
				? `${environment.apiServer}/venues/addvenue?cursor=${encodeURIComponent(cursor)}`
				: `${environment.apiServer}/venues/addvenue`;
			try {
				const res: any = await firstValueFrom(
					this.http.post(url, body, { headers })
				);
				if (Array.isArray(res?.data)) {
					venues.push(...res.data);
				}
				cursor = res?.cursor;
			} catch (error) {
				console.error('Failed to fetch venues page', error);
				break;
			}
		} while (cursor);

		return venues;
	}

	async fees(companyId: any, date: string, serviceId: any, duration: number) {
		// return {
		//   "deposit": 100,
		//   "full_fee": 1143,
		//   "final_balance": 1043,
		//   "payment_code": "WCRG002",
		//   "out_of_hours": 0,
		//   "short_notice": 0,
		//   "full_vat_n": 0,
		//   "full_vat_s": 0,
		//   "dep_vat_n": 0,
		//   "dep_vat_s": 0,
		//   "bal_vat_n": 0,
		//   "bal_vat_s": 0,
		//   "is_bank_holiday": false,
		//   "duration": 60
		// }

		const headers = {
			'Content-Type': 'application/json'
		};

		const bodyData = {
			"service_id": serviceId,
			"date": `${date}`,
			"duration": duration
		};

		// Errors propagate deliberately. This used to `return (error as Error).message`,
		// so /fees returning 404 "No fees configured" arrived as a *string*; callers
		// type-checked it away, coerced the missing hire fee to 0, and charged nothing
		// for a ceremony that has no configured price. A duration with no fee row —
		// Bespoke Extra (duration 120) has none on most Westminster services — must
		// fail visibly rather than price at £0.
		return await firstValueFrom(
			this.http.post(`${environment.apiServer}/company/${companyId}/fees`, bodyData, { headers })
		);
  	}
}